import { WeatherOverview } from "@bienesis/common";
import { Loader2, TriangleAlert } from "lucide-react";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import "twin.macro";

import { useWeatherOverview } from "@/api/weather-overview/queries";

type WeatherOverviewContextValue = {
  data: WeatherOverview;
  isFetching: boolean;
};

const WeatherOverviewContext = createContext<WeatherOverviewContextValue | null>(
  null
);

export const useWeatherOverviewContext = () => {
  const ctx = useContext(WeatherOverviewContext);
  if (!ctx)
    throw new Error(
      "useWeatherOverviewContext must be used within WeatherOverviewDataLoader"
    );
  return ctx;
};

export const WeatherOverviewDataLoader = ({
  areaId,
  children,
}: {
  areaId: string;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching } = useWeatherOverview({
    areaId,
  });

  if (isError) {
    return (
      <div tw="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <div tw="flex items-center justify-center w-12 h-12 rounded-full bg-red-50">
          <TriangleAlert tw="w-6 h-6 text-red-500" />
        </div>
        <p tw="text-sm font-medium text-red-600">
          {t("meteo.forecast.loadError")}
        </p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div tw="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <Loader2 tw="w-6 h-6 text-gray-400 animate-spin" />
        <p tw="text-sm text-gray-500">{t("meteo.forecast.loading")}</p>
      </div>
    );
  }

  return (
    <WeatherOverviewContext.Provider value={{ data, isFetching }}>
      {children}
    </WeatherOverviewContext.Provider>
  );
};
